import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Trash2, RefreshCw, Plus, Upload, User } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Editable Cell Component
const EditableCell = ({ value, onSave, type = 'text', placeholder = '-' }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  const handleSave = () => {
    if (editValue !== value) {
      onSave(editValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value || '');
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type={type}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        className="w-full bg-slate-700 border border-blue-500 rounded px-2 py-1 text-white text-sm focus:outline-none"
      />
    );
  }

  return (
    <div 
      onClick={() => setIsEditing(true)}
      className="cursor-pointer hover:bg-slate-700/50 rounded px-2 py-1 min-h-[28px] flex items-center"
    >
      {value || <span className="text-slate-500 italic">{placeholder}</span>}
    </div>
  );
};

// Avatar Cell with upload
const AvatarCell = ({ value, onSave, name }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  const handleSave = () => {
    if (editValue !== value) {
      onSave(editValue);
    }
    setIsEditing(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/upload/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (data.url) {
        // Construct full URL for the image
        const fullUrl = data.url.startsWith('http') ? data.url : `${API_URL}${data.url}`;
        setEditValue(fullUrl);
        onSave(fullUrl);
      }
    } catch (error) {
      console.error('Error uploading:', error);
    } finally {
      setUploading(false);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="url"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          placeholder="Paste URL or upload"
          className="flex-1 bg-slate-700 border border-blue-500 rounded px-2 py-1 text-white text-xs focus:outline-none"
        />
        <label className="cursor-pointer p-1 bg-slate-600 hover:bg-slate-500 rounded">
          <Upload className="w-3 h-3 text-white" />
          <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
        </label>
      </div>
    );
  }

  return (
    <div 
      onClick={() => setIsEditing(true)}
      className="cursor-pointer hover:bg-slate-700/50 rounded p-1 flex items-center gap-2"
    >
      {value ? (
        <img src={value} alt={name} className="w-8 h-8 rounded-full object-cover" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
          {name?.charAt(0) || '?'}
        </div>
      )}
      {uploading && <span className="text-xs text-blue-400">Uploading...</span>}
    </div>
  );
};

const AdminJudges = () => {
  const [judges, setJudges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [showAddRow, setShowAddRow] = useState(false);
  const [newJudge, setNewJudge] = useState({
    name: '',
    title: '',
    company: '',
    avatar_url: '',
    linkedin_url: '',
    order: 0,
    active: true
  });

  useEffect(() => {
    loadJudges();
  }, []);

  const loadJudges = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/judges`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setJudges(data);
    } catch (error) {
      console.error('Error loading judges:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id, field, value) => {
    setSaving(id);
    try {
      const token = localStorage.getItem('admin_token');
      const judge = judges.find(j => j._id === id);
      const updatedJudge = { ...judge, [field]: value };
      delete updatedJudge._id;
      delete updatedJudge.created_at;
      delete updatedJudge.updated_at;
      
      await fetch(`${API_URL}/api/admin/judges/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatedJudge)
      });
      
      setJudges(judges.map(j => j._id === id ? { ...j, [field]: value } : j));
    } catch (error) {
      console.error('Error updating judge:', error);
    } finally {
      setSaving(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this judge?')) return;
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${API_URL}/api/admin/judges/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setJudges(judges.filter(j => j._id !== id));
    } catch (error) {
      console.error('Error deleting judge:', error);
    }
  };

  const handleAddJudge = async () => {
    if (!newJudge.name.trim()) {
      alert('Name is required');
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_URL}/api/admin/judges`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newJudge)
      });
      
      if (res.ok) {
        loadJudges();
        setNewJudge({
          name: '',
          title: '',
          company: '',
          avatar_url: '',
          linkedin_url: '',
          order: 0,
          active: true
        });
        setShowAddRow(false);
      }
    } catch (error) {
      console.error('Error adding judge:', error);
    }
  };

  return (
    <AdminLayout currentPage="judges">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Judges</h1>
          <p className="text-slate-400 text-sm mt-1">
            Manage event judges. Click any cell to edit. {judges.length} total judges.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={loadJudges}
            variant="outline"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={() => setShowAddRow(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Judge
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400">Loading judges...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-800/50 text-left">
                <tr>
                  <th className="px-4 py-3 text-slate-300 font-medium w-16">Photo</th>
                  <th className="px-4 py-3 text-slate-300 font-medium">Name</th>
                  <th className="px-4 py-3 text-slate-300 font-medium">Title</th>
                  <th className="px-4 py-3 text-slate-300 font-medium">Company</th>
                  <th className="px-4 py-3 text-slate-300 font-medium">LinkedIn</th>
                  <th className="px-4 py-3 text-slate-300 font-medium w-20">Order</th>
                  <th className="px-4 py-3 text-slate-300 font-medium w-20">Active</th>
                  <th className="px-4 py-3 text-slate-300 font-medium w-16">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {/* Add New Row */}
                {showAddRow && (
                  <tr className="bg-blue-900/20">
                    <td className="px-4 py-2">
                      <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center">
                        <User className="w-4 h-4 text-slate-500" />
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={newJudge.name}
                        onChange={(e) => setNewJudge({ ...newJudge, name: e.target.value })}
                        placeholder="Name *"
                        className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-blue-500"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={newJudge.title}
                        onChange={(e) => setNewJudge({ ...newJudge, title: e.target.value })}
                        placeholder="Title"
                        className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-blue-500"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={newJudge.company}
                        onChange={(e) => setNewJudge({ ...newJudge, company: e.target.value })}
                        placeholder="Company"
                        className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-blue-500"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="url"
                        value={newJudge.linkedin_url}
                        onChange={(e) => setNewJudge({ ...newJudge, linkedin_url: e.target.value })}
                        placeholder="LinkedIn URL"
                        className="w-full bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-blue-500"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        value={newJudge.order}
                        onChange={(e) => setNewJudge({ ...newJudge, order: parseInt(e.target.value) || 0 })}
                        className="w-16 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-blue-500"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        checked={newJudge.active}
                        onChange={(e) => setNewJudge({ ...newJudge, active: e.target.checked })}
                        className="w-4 h-4"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex gap-1">
                        <Button size="sm" onClick={handleAddJudge} className="bg-green-600 hover:bg-green-700 px-2 py-1 h-7 text-xs">
                          Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setShowAddRow(false)} className="px-2 py-1 h-7 text-xs text-slate-400">
                          Cancel
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
                
                {/* Existing Judges */}
                {judges.length === 0 && !showAddRow ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                      No judges yet. Click "Add Judge" to create one.
                    </td>
                  </tr>
                ) : (
                  judges.map((judge) => (
                    <tr key={judge._id} className={`hover:bg-slate-800/30 ${saving === judge._id ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-2">
                        <AvatarCell
                          value={judge.avatar_url}
                          name={judge.name}
                          onSave={(val) => handleUpdate(judge._id, 'avatar_url', val)}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <EditableCell
                          value={judge.name}
                          onSave={(val) => handleUpdate(judge._id, 'name', val)}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <EditableCell
                          value={judge.title}
                          onSave={(val) => handleUpdate(judge._id, 'title', val)}
                          placeholder="Add title"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <EditableCell
                          value={judge.company}
                          onSave={(val) => handleUpdate(judge._id, 'company', val)}
                          placeholder="Add company"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <EditableCell
                          value={judge.linkedin_url}
                          onSave={(val) => handleUpdate(judge._id, 'linkedin_url', val)}
                          placeholder="Add LinkedIn"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <EditableCell
                          value={judge.order?.toString() || '0'}
                          type="number"
                          onSave={(val) => handleUpdate(judge._id, 'order', parseInt(val) || 0)}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="checkbox"
                          checked={judge.active}
                          onChange={(e) => handleUpdate(judge._id, 'active', e.target.checked)}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <button
                          onClick={() => handleDelete(judge._id)}
                          className="p-1.5 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminJudges;
